import {
  CREATE_BUSINESS_OPERATION,
  LIST_BUSINESSES_OPERATION,
  CHECK_BUSINESS_APPOINTMENT_OVERLAP_OPERATION,
  CREATE_BUSINESS_APPOINTMENT_OPERATION,
  CREATE_BUSINESS_AVAILABILITY_OPERATION,
  CREATE_BUSINESS_STAFF_OPERATION,
  GET_BUSINESS_CALENDAR_PREFERENCES_OPERATION,
  UPDATE_BUSINESS_CALENDAR_PREFERENCES_OPERATION,
  DELETE_BUSINESS_APPOINTMENT_OPERATION,
  DELETE_BUSINESS_STAFF_OPERATION,
  GET_BUSINESS_OPERATION,
  GET_BUSINESS_DASHBOARD_METRICS_OPERATION,
  GET_BUSINESS_DASHBOARD_COMMUNICATIONS_REPORT_OPERATION,
  LIST_BUSINESS_APPOINTMENTS_OPERATION,
  LIST_BUSINESS_AVAILABILITIES_OPERATION,
  LIST_BUSINESS_STAFF_OPERATION,
  RESTORE_BUSINESS_STAFF_OPERATION,
  UPDATE_BUSINESS_OPERATION,
  UPDATE_BUSINESS_APPOINTMENT_OPERATION,
  UPDATE_BUSINESS_AVAILABILITY_OPERATION,
  UPDATE_BUSINESS_STAFF_OPERATION,
  type GeneratedListBusinessesResponse,
  type GeneratedCreateBusinessAvailabilityRequest,
  type GeneratedCreateBusinessAvailabilityResponse,
  type GeneratedListBusinessAvailabilitiesResponse,
  type GeneratedUpdateBusinessAvailabilityRequest,
  type GeneratedUpdateBusinessAvailabilityResponse,
  type GeneratedCheckAppointmentOverlapRequest,
  type GeneratedCheckAppointmentOverlapResponse,
  type GeneratedCreateAppointmentRequest,
  type GeneratedCreateAppointmentResponse,
  type GeneratedCreateBusinessRequest,
  type GeneratedCreateBusinessResponse,
  type GeneratedCreateBusinessStaffRequest,
  type GeneratedCreateBusinessStaffResponse,
  type GeneratedGetBusinessDashboardMetricsQuery,
  type GeneratedGetBusinessDashboardMetricsResponse,
  type GeneratedGetBusinessDashboardCommunicationsReportQuery,
  type GeneratedGetBusinessDashboardCommunicationsReportResponse,
  type GeneratedGetBusinessResponse,
  type GeneratedListBusinessAppointmentsQuery,
  type GeneratedListBusinessAppointmentsResponse,
  type GeneratedListBusinessStaffQuery,
  type GeneratedListBusinessStaffResponse,
  type GeneratedRestoreBusinessStaffResponse,
  type GeneratedUpdateBusinessRequest,
  type GeneratedUpdateBusinessResponse,
  type GeneratedUpdateAppointmentRequest,
  type GeneratedUpdateAppointmentResponse,
  type GeneratedUpdateBusinessStaffRequest,
  type GeneratedUpdateBusinessStaffResponse,
  type GeneratedGetBusinessCalendarPreferencesResponse,
  type GeneratedUpdateBusinessCalendarPreferencesRequest,
  type GeneratedUpdateBusinessCalendarPreferencesResponse,
} from './generated-business';
import {
  buildQueryString,
  performAuthorizedRequest,
  replacePathParams,
} from './business-request';

// The shared request core and the carved-out domain clients are re-exported so
// existing consumers can keep importing everything from `business-client`.
export {
  BusinessRequestError,
  isBusinessRequestError,
  type BusinessValidationError,
} from './business-request';
export {
  listBusinessServicesRequest,
  createBusinessServiceRequest,
  updateBusinessServiceRequest,
  deleteBusinessServiceRequest,
  restoreBusinessServiceRequest,
} from './services-client';
export {
  listBusinessLoyaltyProgramsRequest,
  createBusinessLoyaltyProgramRequest,
  updateBusinessLoyaltyProgramRequest,
  archiveBusinessLoyaltyProgramRequest,
} from './loyalty-client';
export {
  listBusinessClientsRequest,
  getBusinessClientRequest,
  createBusinessClientRequest,
  updateBusinessClientRequest,
  deleteBusinessClientRequest,
  restoreBusinessClientRequest,
} from './clients-client';
export {
  listBusinessSpacesRequest,
  createBusinessSpaceRequest,
  updateBusinessSpaceRequest,
  archiveBusinessSpaceRequest,
  restoreBusinessSpaceRequest,
} from './spaces-client';
export {
  listBusinessBreaksRequest,
  createBusinessBreakRequest,
  updateBusinessBreakRequest,
  deleteBusinessBreakRequest,
} from './breaks-client';

export async function createBusinessRequest(
  token: string,
  business: GeneratedCreateBusinessRequest | FormData
): Promise<GeneratedCreateBusinessResponse> {
  return performAuthorizedRequest<GeneratedCreateBusinessResponse>(
    token,
    CREATE_BUSINESS_OPERATION,
    {
      body: business,
      networkErrorMessage:
        'Não foi possível ligar ao servidor. Verifique se o backend está disponível.',
      requestErrorMessage: 'Não foi possível criar o negócio agora.',
    }
  );
}

export async function listBusinessesRequest(
  token: string
): Promise<GeneratedListBusinessesResponse> {
  return performAuthorizedRequest<GeneratedListBusinessesResponse>(
    token,
    LIST_BUSINESSES_OPERATION,
    {
      networkErrorMessage:
        'Não foi possível obter os negócios acessíveis. Verifique se o backend está disponível.',
      requestErrorMessage: 'Não foi possível carregar os negócios agora.',
    }
  );
}

export async function updateBusinessRequest(
  token: string,
  businessId: number,
  business: GeneratedUpdateBusinessRequest | FormData
): Promise<GeneratedUpdateBusinessResponse> {
  return performAuthorizedRequest<GeneratedUpdateBusinessResponse>(
    token,
    {
      ...UPDATE_BUSINESS_OPERATION,
      path: replacePathParams(UPDATE_BUSINESS_OPERATION.path, { id: businessId }),
    },
    {
      body: business,
      networkErrorMessage:
        'Não foi possível atualizar o negócio. Verifique se o backend está disponível.',
      requestErrorMessage: 'Não foi possível atualizar o negócio agora.',
    }
  );
}

export async function getBusinessRequest(
  token: string,
  businessId: number
): Promise<GeneratedGetBusinessResponse> {
  return performAuthorizedRequest<GeneratedGetBusinessResponse>(
    token,
    {
      ...GET_BUSINESS_OPERATION,
      path: replacePathParams(GET_BUSINESS_OPERATION.path, { id: businessId }),
    },
    {
      networkErrorMessage:
        'Não foi possível obter os dados do negócio. Verifique se o backend está disponível.',
      requestErrorMessage: 'Não foi possível carregar o negócio agora.',
    }
  );
}

export async function listBusinessAppointmentsRequest(
  token: string,
  businessId: number,
  query: Partial<GeneratedListBusinessAppointmentsQuery> & {
    space_id?: number;
  } = {}
): Promise<GeneratedListBusinessAppointmentsResponse> {
  return performAuthorizedRequest<GeneratedListBusinessAppointmentsResponse>(
    token,
    {
      ...LIST_BUSINESS_APPOINTMENTS_OPERATION,
      path:
        replacePathParams(LIST_BUSINESS_APPOINTMENTS_OPERATION.path, {
          business_id: businessId,
        }) +
        buildQueryString({
          date: query.date,
          start_date: query.start_date,
          end_date: query.end_date,
          timezone: query.timezone,
          staff_id: query.staff_id,
          space_id: query.space_id,
          status: query.status,
        }),
    },
    {
      networkErrorMessage:
        'Não foi possível obter as marcações. Verifique se o backend está disponível.',
      requestErrorMessage: 'Não foi possível carregar as marcações agora.',
    }
  );
}

export async function getBusinessDashboardMetricsRequest(
  token: string,
  businessId: number,
  query: GeneratedGetBusinessDashboardMetricsQuery & { space_id?: number }
): Promise<GeneratedGetBusinessDashboardMetricsResponse> {
  return performAuthorizedRequest<GeneratedGetBusinessDashboardMetricsResponse>(
    token,
    {
      ...GET_BUSINESS_DASHBOARD_METRICS_OPERATION,
      path:
        replacePathParams(GET_BUSINESS_DASHBOARD_METRICS_OPERATION.path, {
          business_id: businessId,
        }) +
        buildQueryString({
          start_date: query.start_date,
          end_date: query.end_date,
          anchor_date: query.anchor_date,
          timezone: query.timezone,
          staff_id: query.staff_id,
          space_id: query.space_id,
        }),
    },
    {
      networkErrorMessage:
        'Não foi possível obter as métricas do dashboard. Verifique se o backend está disponível.',
      requestErrorMessage: 'Não foi possível carregar as métricas do dashboard agora.',
    }
  );
}

export async function getBusinessDashboardCommunicationsReportRequest(
  token: string,
  businessId: number,
  query: GeneratedGetBusinessDashboardCommunicationsReportQuery
): Promise<GeneratedGetBusinessDashboardCommunicationsReportResponse> {
  return performAuthorizedRequest<GeneratedGetBusinessDashboardCommunicationsReportResponse>(
    token,
    {
      ...GET_BUSINESS_DASHBOARD_COMMUNICATIONS_REPORT_OPERATION,
      path:
        replacePathParams(GET_BUSINESS_DASHBOARD_COMMUNICATIONS_REPORT_OPERATION.path, {
          business_id: businessId,
        }) +
        buildQueryString({
          start_date: query.start_date,
          end_date: query.end_date,
          space_id: query.space_id,
        }),
    },
    {
      networkErrorMessage:
        'Não foi possível obter o relatório de comunicações. Verifique se o backend está disponível.',
      requestErrorMessage: 'Não foi possível carregar o relatório de comunicações agora.',
    }
  );
}


export async function createBusinessAppointmentRequest(
  token: string,
  businessId: number,
  appointment: GeneratedCreateAppointmentRequest
): Promise<GeneratedCreateAppointmentResponse> {
  return performAuthorizedRequest<GeneratedCreateAppointmentResponse>(
    token,
    {
      ...CREATE_BUSINESS_APPOINTMENT_OPERATION,
      path: replacePathParams(CREATE_BUSINESS_APPOINTMENT_OPERATION.path, {
        business_id: businessId,
      }),
    },
    {
      body: appointment,
      networkErrorMessage:
        'Não foi possível criar a marcação. Verifique se o backend está disponível.',
      requestErrorMessage: 'Não foi possível criar a marcação agora.',
    }
  );
}

export async function checkBusinessAppointmentOverlapRequest(
  token: string,
  businessId: number,
  body: GeneratedCheckAppointmentOverlapRequest
): Promise<GeneratedCheckAppointmentOverlapResponse> {
  return performAuthorizedRequest<GeneratedCheckAppointmentOverlapResponse>(
    token,
    {
      ...CHECK_BUSINESS_APPOINTMENT_OVERLAP_OPERATION,
      path: replacePathParams(CHECK_BUSINESS_APPOINTMENT_OVERLAP_OPERATION.path, {
        business_id: businessId,
      }),
    },
    {
      body,
      networkErrorMessage:
        'Não foi possível verificar conflitos de horário. Verifique se o backend está disponível.',
      requestErrorMessage: 'Não foi possível verificar conflitos de horário agora.',
    }
  );
}

export async function updateBusinessAppointmentRequest(
  token: string,
  businessId: number,
  appointmentId: number,
  appointment: GeneratedUpdateAppointmentRequest
): Promise<GeneratedUpdateAppointmentResponse> {
  return performAuthorizedRequest<GeneratedUpdateAppointmentResponse>(
    token,
    {
      ...UPDATE_BUSINESS_APPOINTMENT_OPERATION,
      path: replacePathParams(UPDATE_BUSINESS_APPOINTMENT_OPERATION.path, {
        business_id: businessId,
        id: appointmentId,
      }),
    },
    {
      body: appointment,
      networkErrorMessage:
        'Não foi possível atualizar a marcação. Verifique se o backend está disponível.',
      requestErrorMessage: 'Não foi possível atualizar a marcação agora.',
    }
  );
}

export async function deleteBusinessAppointmentRequest(
  token: string,
  businessId: number,
  appointmentId: number
) {
  await performAuthorizedRequest(
    token,
    {
      ...DELETE_BUSINESS_APPOINTMENT_OPERATION,
      path: replacePathParams(DELETE_BUSINESS_APPOINTMENT_OPERATION.path, {
        business_id: businessId,
        id: appointmentId,
      }),
    },
    {
      networkErrorMessage:
        'Não foi possível eliminar a marcação. Verifique se o backend está disponível.',
      requestErrorMessage: 'Não foi possível eliminar a marcação agora.',
    }
  );
}

export async function listBusinessStaffRequest(
  token: string,
  businessId: number,
  query: Partial<GeneratedListBusinessStaffQuery> & { space_id?: number } = {}
): Promise<GeneratedListBusinessStaffResponse> {
  return performAuthorizedRequest<GeneratedListBusinessStaffResponse>(
    token,
    {
      ...LIST_BUSINESS_STAFF_OPERATION,
      path:
        replacePathParams(LIST_BUSINESS_STAFF_OPERATION.path, {
          business_id: businessId,
        }) +
        buildQueryString({
          email: query.email,
          phone: query.phone,
          name: query.name,
          q: query.q,
          public_booking_enabled: query.public_booking_enabled,
          status: query.status,
          space_id: query.space_id,
          page: query.page,
          limit: query.limit,
        }),
    },
    {
      networkErrorMessage:
        'Não foi possível obter a equipa. Verifique se o backend está disponível.',
      requestErrorMessage: 'Não foi possível carregar a equipa agora.',
    }
  );
}

export async function createBusinessStaffRequest(
  token: string,
  businessId: number,
  staff: GeneratedCreateBusinessStaffRequest
): Promise<GeneratedCreateBusinessStaffResponse> {
  return performAuthorizedRequest<GeneratedCreateBusinessStaffResponse>(
    token,
    {
      ...CREATE_BUSINESS_STAFF_OPERATION,
      path: replacePathParams(CREATE_BUSINESS_STAFF_OPERATION.path, {
        business_id: businessId,
      }),
    },
    {
      body: staff,
      networkErrorMessage:
        'Não foi possível criar o membro de equipa. Verifique se o backend está disponível.',
      requestErrorMessage: 'Não foi possível criar o membro de equipa agora.',
    }
  );
}

export async function updateBusinessStaffRequest(
  token: string,
  businessId: number,
  staffId: number,
  staff: GeneratedUpdateBusinessStaffRequest
): Promise<GeneratedUpdateBusinessStaffResponse> {
  return performAuthorizedRequest<GeneratedUpdateBusinessStaffResponse>(
    token,
    {
      ...UPDATE_BUSINESS_STAFF_OPERATION,
      path: replacePathParams(UPDATE_BUSINESS_STAFF_OPERATION.path, {
        business_id: businessId,
        id: staffId,
      }),
    },
    {
      body: staff,
      networkErrorMessage:
        'Não foi possível atualizar o membro de equipa. Verifique se o backend está disponível.',
      requestErrorMessage: 'Não foi possível atualizar o membro de equipa agora.',
    }
  );
}

export async function deleteBusinessStaffRequest(
  token: string,
  businessId: number,
  staffId: number
) {
  await performAuthorizedRequest(
    token,
    {
      ...DELETE_BUSINESS_STAFF_OPERATION,
      path: replacePathParams(DELETE_BUSINESS_STAFF_OPERATION.path, {
        business_id: businessId,
        id: staffId,
      }),
    },
    {
      networkErrorMessage:
        'Não foi possível arquivar o membro de equipa. Verifique se o backend está disponível.',
      requestErrorMessage: 'Não foi possível arquivar o membro de equipa agora.',
    }
  );
}

export async function restoreBusinessStaffRequest(
  token: string,
  businessId: number,
  staffId: number
): Promise<GeneratedRestoreBusinessStaffResponse> {
  return performAuthorizedRequest<GeneratedRestoreBusinessStaffResponse>(
    token,
    {
      ...RESTORE_BUSINESS_STAFF_OPERATION,
      path: replacePathParams(RESTORE_BUSINESS_STAFF_OPERATION.path, {
        business_id: businessId,
        id: staffId,
      }),
    },
    {
      networkErrorMessage:
        'Não foi possível restaurar o membro de equipa. Verifique se o backend está disponível.',
      requestErrorMessage: 'Não foi possível restaurar o membro de equipa agora.',
    }
  );
}

export async function listBusinessAvailabilitiesRequest(
  token: string,
  businessId: number,
  query: { space_id?: number } = {}
): Promise<GeneratedListBusinessAvailabilitiesResponse> {
  return performAuthorizedRequest<GeneratedListBusinessAvailabilitiesResponse>(
    token,
    {
      ...LIST_BUSINESS_AVAILABILITIES_OPERATION,
      path:
        replacePathParams(LIST_BUSINESS_AVAILABILITIES_OPERATION.path, {
          business_id: businessId,
        }) +
        buildQueryString({
          space_id: query.space_id,
        }),
    },
    {
      networkErrorMessage:
        'Não foi possível obter os horários. Verifique se o backend está disponível.',
      requestErrorMessage: 'Não foi possível carregar os horários agora.',
    }
  );
}

export async function createBusinessAvailabilityRequest(
  token: string,
  businessId: number,
  availability: GeneratedCreateBusinessAvailabilityRequest
): Promise<GeneratedCreateBusinessAvailabilityResponse> {
  return performAuthorizedRequest<GeneratedCreateBusinessAvailabilityResponse>(
    token,
    {
      ...CREATE_BUSINESS_AVAILABILITY_OPERATION,
      path: replacePathParams(CREATE_BUSINESS_AVAILABILITY_OPERATION.path, {
        business_id: businessId,
      }),
    },
    {
      body: availability,
      networkErrorMessage:
        'Não foi possível criar o horário. Verifique se o backend está disponível.',
      requestErrorMessage: 'Não foi possível criar o horário agora.',
    }
  );
}

export async function updateBusinessAvailabilityRequest(
  token: string,
  businessId: number,
  availabilityId: number,
  availability: GeneratedUpdateBusinessAvailabilityRequest
): Promise<GeneratedUpdateBusinessAvailabilityResponse> {
  return performAuthorizedRequest<GeneratedUpdateBusinessAvailabilityResponse>(
    token,
    {
      ...UPDATE_BUSINESS_AVAILABILITY_OPERATION,
      path: replacePathParams(UPDATE_BUSINESS_AVAILABILITY_OPERATION.path, {
        business_id: businessId,
        id: availabilityId,
      }),
    },
    {
      body: availability,
      networkErrorMessage:
        'Não foi possível atualizar o horário. Verifique se o backend está disponível.',
      requestErrorMessage: 'Não foi possível atualizar o horário agora.',
    }
  );
}

export async function getCalendarPreferencesRequest(
  token: string,
  businessId: number,
): Promise<GeneratedGetBusinessCalendarPreferencesResponse> {
  return performAuthorizedRequest<GeneratedGetBusinessCalendarPreferencesResponse>(
    token,
    {
      ...GET_BUSINESS_CALENDAR_PREFERENCES_OPERATION,
      path: replacePathParams(GET_BUSINESS_CALENDAR_PREFERENCES_OPERATION.path, {
        business_id: businessId,
      }),
    },
    {
      networkErrorMessage:
        'Não foi possível obter as preferências do calendário. Verifique se o backend está disponível.',
      requestErrorMessage: 'Não foi possível carregar as preferências do calendário agora.',
    }
  );
}

export async function updateCalendarPreferencesRequest(
  token: string,
  businessId: number,
  body: GeneratedUpdateBusinessCalendarPreferencesRequest,
): Promise<GeneratedUpdateBusinessCalendarPreferencesResponse> {
  return performAuthorizedRequest<GeneratedUpdateBusinessCalendarPreferencesResponse>(
    token,
    {
      ...UPDATE_BUSINESS_CALENDAR_PREFERENCES_OPERATION,
      path: replacePathParams(UPDATE_BUSINESS_CALENDAR_PREFERENCES_OPERATION.path, {
        business_id: businessId,
      }),
    },
    {
      body,
      networkErrorMessage:
        'Não foi possível guardar as preferências do calendário. Verifique se o backend está disponível.',
      requestErrorMessage: 'Não foi possível guardar as preferências do calendário agora.',
    }
  );
}
